import { type ReactNode, useState } from 'react';
import { CalendarIcon, Check, ChevronDown, Info, Lock } from 'lucide-react';
import { Calendar } from '@components/ui/calendar';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@components/ui/tooltip';
import { cn } from '@lib/utils';

export const controlBase =
  'w-full rounded-md border bg-[hsl(var(--field-bg))] px-3 text-sm text-foreground outline-none transition-colors duration-150 placeholder:text-muted-foreground/80 hover:border-[hsl(var(--ring))] focus-visible:border-[hsl(var(--ring))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring)/0.3)] disabled:cursor-not-allowed disabled:opacity-50';

interface ShellProps {
  id: string;
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  className?: string;
  tooltip?: string;
  children: ReactNode;
}

export function FieldShell({ id, label, required, hint, error, className, tooltip, children }: ShellProps) {
  return (
    <div className={cn('flex flex-col min-w-0 gap-1.5', className)}>
      <label htmlFor={id} className="flex items-baseline gap-1 text-[13px] font-medium text-foreground">
        <span>{label}</span>
        {required ? (
          <span className="text-destructive" aria-hidden="true">
            *
          </span>
        ) : null}
        {tooltip ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`מידע לגבי ${label}`}
                  className="inline-flex text-muted-foreground/70 transition-colors duration-150 hover:text-foreground"
                >
                  <Info className="size-3.5" aria-hidden="true" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-64 text-start leading-relaxed">
                {tooltip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : null}
      </label>
      {children}
      {error ? (
        <p
          id={`${id}-error`}
          role="alert"
          className="text-xs font-medium text-destructive mt-0.5 leading-snug"
        >
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs leading-relaxed text-muted-foreground mt-0.5">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

interface TextFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  hint?: string;
  error?: string;
  type?: 'text' | 'email' | 'tel' | 'date';
  inputMode?: 'text' | 'numeric' | 'tel' | 'email';
  placeholder?: string;
  maxLength?: number;
  dir?: 'rtl' | 'ltr';
  className?: string;
  min?: string;
  disabled?: boolean;
  locked?: boolean;
}

export function TextField({
  id,
  label,
  value,
  onChange,
  onBlur,
  required,
  hint,
  error,
  type = 'text',
  inputMode,
  placeholder,
  maxLength,
  dir,
  className,
  min,
  disabled,
  locked,
}: TextFieldProps) {
  return (
    <FieldShell id={id} label={label} required={required} hint={hint} error={error} className={className}>
      <div className="relative min-w-0">
        <input
          id={id}
          type={type}
          value={value}
          dir={dir}
          min={min}
          disabled={disabled}
          readOnly={locked}
          inputMode={inputMode}
          placeholder={placeholder}
          maxLength={maxLength}
          aria-required={required ? true : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          className={cn(
            controlBase,
            'h-11',
            locked ? 'pr-9 text-muted-foreground' : '',
            error ? 'border-destructive' : 'border-[hsl(var(--field-border))]',
          )}
        />
        {locked ? (
          <Lock
            aria-hidden="true"
            className="pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
        ) : null}
      </div>
    </FieldShell>
  );
}

export type SelectOption = string | { label: string; value: string };

interface SelectFieldProps {
  id: string;
  label: string;
  value: string;
  options: (string | { label: string; value: string })[];
  onChange: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  hint?: string;
  error?: string;
  placeholder?: string;
  className?: string;
  tooltip?: string;
}

export function SelectField({
  id,
  label,
  value,
  options,
  onChange,
  onBlur,
  required,
  hint,
  error,
  placeholder = 'נא לבחור',
  className,
  tooltip,
}: SelectFieldProps) {
  return (
    <FieldShell
      id={id}
      label={label}
      required={required}
      hint={hint}
      error={error}
      className={className}
      tooltip={tooltip}
    >
      <div className="relative min-w-0">
        <select
          id={id}
          value={value}
          aria-required={required ? true : undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
          onChange={(event) => onChange(event.target.value)}
          onBlur={onBlur}
          className={cn(
            controlBase,
            'h-11 appearance-none pe-9',
            error ? 'border-destructive' : 'border-[hsl(var(--field-border))]',
          )}
        >
          <option value="">{placeholder}</option>
          {options.map((option) => {
            const optValue = typeof option === 'string' ? option : option.value;
            const optLabel = typeof option === 'string' ? option : option.label;
            return (
              <option key={optValue} value={optValue}>
                {optLabel}
              </option>
            );
          })}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute end-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
      </div>
    </FieldShell>
  );
}

interface SearchableSelectFieldProps {
  id: string;
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  hint?: string;
  error?: string;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
  disabled?: boolean;
}

/** Combobox variant of SelectField for long option lists — adds a filter-as-you-type search box. */
export function SearchableSelectField({
  id,
  label,
  value,
  options,
  onChange,
  onBlur,
  required,
  hint,
  error,
  placeholder = 'נא לבחור',
  emptyMessage = 'לא נמצאו תוצאות',
  className,
  disabled,
}: SearchableSelectFieldProps) {
  const [open, setOpen] = useState(false);

  return (
    <FieldShell id={id} label={label} required={required} hint={hint} error={error} className={className}>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) onBlur?.();
        }}
      >
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            disabled={disabled}
            aria-haspopup="listbox"
            aria-expanded={open}
            aria-controls={`${id}-listbox`}
            aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
            className={cn(
              controlBase,
              'h-11 flex items-center justify-between gap-2 text-start',
              error ? 'border-destructive' : 'border-[hsl(var(--field-border))]',
              !value ? 'text-muted-foreground/80' : '',
            )}
          >
            <span className="min-w-0 truncate">{value || placeholder}</span>
            <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
          <Command loop>
            <CommandInput placeholder="חיפוש…" />
            <CommandList id={`${id}-listbox`} className="max-h-64">
              <CommandEmpty className="px-3 py-4 text-sm text-muted-foreground">{emptyMessage}</CommandEmpty>
              <CommandGroup>
                {options.map((option) => (
                  <CommandItem
                    key={option}
                    value={option}
                    onSelect={() => {
                      onChange(option);
                      setOpen(false);
                    }}
                  >
                    <Check className={cn('size-4', value === option ? 'opacity-100' : 'opacity-0')} aria-hidden="true" />
                    <span className="min-w-0 truncate">{option}</span>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </FieldShell>
  );
}

interface TextAreaFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string;
  rows?: number;
  maxLength?: number;
}

export function TextAreaField({ id, label, value, onChange, hint, rows = 4, maxLength = 600 }: TextAreaFieldProps) {
  return (
    <FieldShell id={id} label={label} hint={hint}>
      <textarea
        id={id}
        rows={rows}
        value={value}
        maxLength={maxLength}
        aria-describedby={hint ? `${id}-hint` : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={cn(controlBase, 'resize-y border-[hsl(var(--field-border))] py-2 leading-relaxed')}
      />
    </FieldShell>
  );
}

interface DateFieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  required?: boolean;
  hint?: string;
  error?: string;
  min?: string;
  max?: string;
  fromYear?: number;
  toYear?: number;
  defaultMonth?: Date;
  captionLayout?: 'label' | 'dropdown' | 'dropdown-buttons';
  className?: string;
}

function parseIsoDate(value: string): Date | undefined {
  if (!value) return undefined;
  const parts = value.split('-').map(Number);
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return undefined;
  const [y, m, d] = parts;
  return new Date(y!, (m ?? 1) - 1, d);
}

function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatDisplayDate(value: string): string {
  const date = parseIsoDate(value);
  if (!date) return '';
  const d = String(date.getDate()).padStart(2, '0');
  const m = String(date.getMonth() + 1).padStart(2, '0');
  return `${d}/${m}/${date.getFullYear()}`;
}

const MONTH_NAMES_HE = [
  'ינואר',
  'פברואר',
  'מרץ',
  'אפריל',
  'מאי',
  'יוני',
  'יולי',
  'אוגוסט',
  'ספטמבר',
  'אוקטובר',
  'נובמבר',
  'דצמבר',
];

export function DateField({
  id,
  label,
  value,
  onChange,
  onBlur,
  required,
  hint,
  error,
  min,
  max,
  fromYear = 1930,
  toYear,
  defaultMonth,
  className,
}: DateFieldProps) {
  const [open, setOpen] = useState(false);
  const selected = parseIsoDate(value);
  const minDate = min ? parseIsoDate(min) : undefined;
  const maxDate = max ? parseIsoDate(max) : undefined;

  const currentYear = new Date().getFullYear();
  const effectiveToYear = toYear ?? (maxDate ? maxDate.getFullYear() : currentYear + 5);

  const initialMonth = selected ?? defaultMonth ?? maxDate ?? new Date();
  const [viewMonth, setViewMonth] = useState<Date>(initialMonth);

  // Sync viewMonth when popover opens or defaultMonth/selected changes
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setViewMonth(selected ?? defaultMonth ?? maxDate ?? new Date());
    } else {
      onBlur?.();
    }
  };

  const years: number[] = [];
  for (let y = effectiveToYear; y >= fromYear; y--) {
    years.push(y);
  }

  return (
    <FieldShell id={id} label={label} required={required} hint={hint} error={error} className={className}>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
            className={cn(
              controlBase,
              'h-11 flex items-center justify-between gap-2 text-start',
              error ? 'border-destructive' : 'border-[hsl(var(--field-border))]',
              !value ? 'text-muted-foreground/80' : '',
            )}
          >
            <span className="tabular-nums">{value ? formatDisplayDate(value) : 'dd/mm/yyyy'}</span>
            <CalendarIcon aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-3">
          {/* Quick Year & Month selectors for fast navigation across decades */}
          <div className="mb-3 flex items-center justify-between gap-2 border-b border-[hsl(var(--rule-soft))] pb-2.5">
            <select
              aria-label="בחירת חודש"
              value={viewMonth.getMonth()}
              onChange={(e) => {
                const newMonth = Number(e.target.value);
                const next = new Date(viewMonth.getFullYear(), newMonth, 1);
                setViewMonth(next);
              }}
              className="h-8 flex-1 rounded border border-[hsl(var(--field-border))] bg-[hsl(var(--surface-1))] px-2 text-xs font-medium text-foreground focus:border-primary focus:outline-none"
            >
              {MONTH_NAMES_HE.map((name, idx) => (
                <option key={name} value={idx}>
                  {name} ({idx + 1})
                </option>
              ))}
            </select>

            <select
              aria-label="בחירת שנה"
              value={viewMonth.getFullYear()}
              onChange={(e) => {
                const newYear = Number(e.target.value);
                const next = new Date(newYear, viewMonth.getMonth(), 1);
                setViewMonth(next);
              }}
              className="h-8 flex-1 rounded border border-[hsl(var(--field-border))] bg-[hsl(var(--surface-1))] px-2 text-xs font-medium text-foreground focus:border-primary focus:outline-none"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          <Calendar
            mode="single"
            selected={selected}
            month={viewMonth}
            onMonthChange={setViewMonth}
            disabled={[
              ...(minDate ? [{ before: minDate }] : []),
              ...(maxDate ? [{ after: maxDate }] : []),
            ]}
            onSelect={(date) => {
              if (!date) return;
              onChange(toIsoDate(date));
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
    </FieldShell>
  );
}
